import { createContext, useContext } from 'react';

import type { UserInfo } from '@/features/register/context/register.context';

import type { OtpState } from '../hooks/useKioskOtp';

export type RegisterContextValue = {
  registerInitialData: Partial<UserInfo>;
  setRegisterInitialData: React.Dispatch<React.SetStateAction<Partial<UserInfo>>>;
  formError: string | null;
  setFormError: React.Dispatch<React.SetStateAction<string | null>>;
  /** True while syncing auth from dashboard (useDashboardUserSync); shows loading in KioskFlowLayout. No auto-redirect to /interview — user goes through flow and taps onboarding to go to /interview when already auth. */
  isAutoStartingWithSession: boolean;
  /** True while the register + OTP flow is processing (checkUserExists, login, sendOTP). Shows loading animation on phone screen. */
  isRegistering: boolean;
  /** If userInfos provided: register flow (submit + start OTP flow). If not: use session (user already synced via useDashboardUserSync), create interview, navigate /interview. */
  startInterview: (userInfos?: UserInfo) => Promise<void>;
  handleRegisterPhoneNext: (userInfos: UserInfo) => Promise<void>;
  /** OTP flow state (shared so OtpPage reads same state as register flow that set it). */
  kioskOtpState: OtpState | null;
  handleOtpVerificationSuccess: () => Promise<void>;
  handleOtpResend: () => Promise<void>;
  handleEmailFallback: (email: string) => Promise<void>;
  /** EMAIL mode: send the OTP by SMS instead (asks the phone when it was skipped). */
  handleSmsFallback: (phone?: string) => Promise<void>;
  handleClearSmsError: () => void;
  handleOtpBack: () => void;
};

export const RegisterContext = createContext<RegisterContextValue | null>(null);

export function useRegisterContext(): RegisterContextValue {
  const ctx = useContext(RegisterContext);
  if (!ctx) throw new Error('useRegisterContext must be used within RegisterProvider');
  return ctx;
}
